import React from 'react';
import { Navigate } from 'react-router-dom';

import LoadingSkeleton from '@/shared/components/ui/LoadingSkeleton';
import { useAuth } from '@/shared/hooks/useAuth';
import { useFeatures } from '@/shared/hooks/useFeatures';
import { APP_ROUTES } from '@/shared/routes/paths';
import type { FeatureKey } from '@/shared/types/features';

interface FeatureGuardProps {
  featureKey: FeatureKey | FeatureKey[];
  /** `any` (default): one enabled key is enough. `all`: every key must be. */
  match?: 'any' | 'all';
  children: React.ReactNode;
  redirectTo?: string;
}

const FeatureGuard: React.FC<FeatureGuardProps> = ({
  featureKey,
  match = 'any',
  children,
  redirectTo
}) => {
  const { isLoading } = useAuth();
  const { isFeatureEnabled, isAnyFeatureEnabled } = useFeatures();

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const allowed =
    match === 'all'
      ? (Array.isArray(featureKey) ? featureKey : [featureKey]).every(
          isFeatureEnabled
        )
      : isAnyFeatureEnabled(featureKey);

  if (!allowed) {
    return <Navigate to={redirectTo || APP_ROUTES.NOT_FOUND} replace={true} />;
  }

  return <>{children}</>;
};

export default FeatureGuard;
